import { ISearchService } from "./ISearchService";
import { ThrottleResponse } from "../Models/ThrottleResponse";
import { IFramework, IWebServiceManager, HttpMethod, IUserContext } from "@truckdown/framework";
import { SearchRequestBase } from "../Models/SearchRequestBase";
import { SearchResult } from "../Models/SearchResult";

interface IResponse {
    ok: boolean,
    message: string
}

class SearchService implements ISearchService {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
    }

    readonly manager: IWebServiceManager;
    private readonly basePath: string = '/locations/search';

    private getEmptyResult(): SearchResult {
        return {
            id: 'id',
            title: 'Search request not found',
            seed: 0,
            page: 1,
            privateListings: [],
            preferredListings: [],
            listings: [],
            total: 0,
            amenities: []
        };
    };

    private createResponse<T>(result: T): ThrottleResponse<T> {
        return {
            blocked: false,
            captcha: false,
            warning: '',
            result: result
        };
    }

    async searchTitle(request: SearchRequestBase): Promise<string> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/locations/search-title',
            method: HttpMethod.POST
        }, request);

        var res = await this.manager.get<IResponse>(req);
        if (res.ok) {
            return res.message;
        }
        console.log('Error getting search title', res);
        return '';
    }

    private throttleWarningHeader = 'x-throttle-warn';

    search(request: string | SearchRequestBase): Promise<ThrottleResponse<SearchResult>> {
        if (typeof request === 'string') {
            return this.searchWithLog(request);
        }

        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath,
            method: HttpMethod.POST
        }, request);

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((json) => {
                            var j = json as ThrottleResponse<SearchResult>;
                            j.warning = res.headers.get(this.throttleWarningHeader) ?? '';
                            return j;
                        });
                }

                if (res.status == 429) {
                    return {
                        blocked: false,
                        captcha: true,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: this.getEmptyResult()
                    }
                }

                if (res.status == 403) {
                    return {
                        blocked: true,
                        captcha: false,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: this.getEmptyResult()
                    }
                }

                console.log("Error running search", res);
                return this.createResponse(this.getEmptyResult());
            });
    }

    private searchWithLog(logId: string): Promise<ThrottleResponse<SearchResult>> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + encodeURIComponent(logId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((json) => {
                            var j = json as ThrottleResponse<SearchResult>;
                            j.warning = res.headers.get(this.throttleWarningHeader) ?? '';
                            return j;
                        });
                }

                if (res.status == 429) {
                    return {
                        blocked: false,
                        captcha: true,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: this.getEmptyResult()
                    }
                }

                if (res.status == 403) {
                    return {
                        blocked: true,
                        captcha: false,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: this.getEmptyResult()
                    }
                }

                console.log("Error running search for search log  " + logId, res);
                return this.createResponse(this.getEmptyResult());
            });
    }
}

export default (framework: IFramework) => {
    framework.registerTransient<ISearchService>('ISearchService', (frm) => {
        return new SearchService(frm);
    });
}
